/**
 * Turns a rejected patient save into something the form can point at.
 *
 * The backend rejects a save with a message key, not a field. Shown as a toast
 * alone that leaves the user to work out which of four tabs and forty fields it
 * was about — so the key is translated here into the control to mark and the step
 * that control lives on, and the form marks the field, flags the step and jumps
 * there.
 *
 * Anything not listed falls through to `null`, and the caller shows its usual
 * message. That is deliberate: a wrong guess would put a red box on a field that
 * is fine, which is worse than a message with no field attached.
 */

/** Steps of the patient form, in the order the tabs appear. */
export type PatientFormStep = 'mainInfo' | 'additionalInfo' | 'comment' | 'device';

export interface PatientSaveFieldError {
    /** Step the offending control lives on, so the right tab can be flagged. */
    step: PatientFormStep;
    /** Name of the form control to mark invalid. */
    control: string;
    /**
     * Shown under the field. Deliberately terse: the strip beneath a control is one
     * line tall and a wrapped message overlaps the row below it, so the field says
     * what is wrong and the label beside it says which field.
     */
    message: string;
    /** The full sentence, for the toast, which has room for it. */
    toast: string;
}

/**
 * Keyed by the message the API returns. Kept as data rather than a chain of ifs so
 * that adding a field-level error is one line, and so the set can be asserted in a
 * test without going through a component.
 */
const FIELD_ERRORS: Record<string, PatientSaveFieldError> = {
    'PATIENT.ERRORS.PATIENT_ID_ALREADY_EXISTS': {
        step: 'mainInfo',
        control: 'patientId',
        message: 'Already in use',
        toast: 'This patient ID is already in use.',
    },
    'USER.ERRORS.EMAILS_EXISTS': {
        step: 'mainInfo',
        control: 'email',
        message: 'Already in use',
        toast: 'This email address is already used by another account.',
    },
    'RESEARCH_CONSENT.ERRORS.PATIENT_EMAIL_REQUIRED': {
        step: 'mainInfo',
        control: 'email',
        message: 'Required for the IRB form',
        toast: 'An email address is required to send the IRB form.',
    },
};

/** Tab labels, in tab order — used to flag and to name the step in a message. */
export const PATIENT_STEP_LABELS: Record<PatientFormStep, string> = {
    mainInfo: 'Main Information',
    additionalInfo: 'Additional Information',
    comment: 'Add Comment',
    device: 'Device',
};

/**
 * The field a failed save was about, or null when the API named something this
 * form cannot point at.
 */
export function mapPatientSaveError(error: any): PatientSaveFieldError | null {
    const message = error?.error?.message;
    if (typeof message !== 'string') {
        return null;
    }
    return FIELD_ERRORS[message] ?? null;
}
